import { cn } from "@utils/tw"
import { Chip } from "./Chip"
import { ComponentPropsWithoutRef, forwardRef, useMemo } from "react"
import { Link } from "eddev/routing"
import { CARET_RIGHT } from "./Icons"
import { ContainerReset } from "@components/layout/GridReset"
import { useAppData } from "eddev/hooks"
import { BreadcrumbsPageFragment, MenuLocationEnum } from "../../types.graphql"

type AncestorT = {
  uri: string
  title: string
  children?: {
    uri: string
    title: string
  }[]
}

function normalize(url: string) {
  return url.replace(/^https?:\/\/[^\/]+\//, "/").replace(/\/?(\?.*)?(\#.*)?$/, "")
}

export const Breadcrumbs = ({
  uri,
  page,
  nonLinkExtra,
}: {
  uri: string
  page: BreadcrumbsPageFragment | null | undefined
  nonLinkExtra?: string
}) => {
  const mainMenu = useAppData(
    (a) => a.menus?.nodes.find((n) => n.locations?.includes(MenuLocationEnum.Main))?.menuItems?.nodes,
  )

  const ancestors: AncestorT[] | [] = useMemo(() => {
    // Uses the main menu to find the children of a given URL, returning null if none are found
    function getMenuChildren(url: string) {
      let result: { uri: string; title: string }[] = []
      const n1 = normalize(url)
      const recurse = (items: any[]) => {
        for (let item of items) {
          const n2 = normalize(item.url)
          if (n1 === n2 && n1 !== "" && item.childItems?.nodes?.length > 0) {
            result = item.childItems?.nodes?.map((child: any) => ({
              uri: child.url,
              title: child.label,
            }))
            break
          } else {
            recurse(item.childItems?.nodes ?? [])
          }
        }
      }
      recurse(mainMenu ?? [])
      if (result?.length === 0) return null
      return result
    }

    if (page?.ancestors?.nodes) {
      const filtered = page.ancestors.nodes.filter((node) => node.__typename === "Page")

      return filtered
        .map((ancestor: any) => {
          return {
            uri: ancestor.uri,
            title: ancestor.title,
            children:
              getMenuChildren(ancestor.uri) ??
              ancestor.children.nodes.map((child: any) => ({
                uri: child.uri,
                title: child.title,
              })),
          }
        })
        .reverse()
    }
    return []
  }, [page, mainMenu])

  const children: AncestorT[] | [] = useMemo(() => {
    if (page?.children?.nodes) {
      const filtered = page.children.nodes.filter((node) => node.__typename === "Page")

      return filtered
        .map((ancestor: any) => ({
          uri: ancestor.uri,
          title: ancestor.title,
        }))
        .reverse()
    }
    return []
  }, [page])

  if (page) {
    return (
      <BreadcrumbsContainer>
        <div className="grid grid-cols-12 xxl:px-0 !w-[100vw] xl:!max-w-[1600px] xxl:!max-w-[1720px] mx-auto gap-6">
          <div className="col-span-12">
            <ul>
              {ancestors.map((ancestor, i) => (
                <StyledLi key={i} style={{ maxWidth: `calc(100% * (1 / ${ancestors.length + 1}))` }}>
                  <Chip color="bgColor" href={ancestor.uri}>
                    {ancestor?.title}
                  </Chip>

                  {ancestor?.children && (
                    <Dropdown>
                      <ul>
                        {ancestor.children.map((child, i) => (
                          <li key={i}>
                            <Link href={child.uri}>
                              {CARET_RIGHT} <span>{child.title}</span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </Dropdown>
                  )}
                </StyledLi>
              ))}

              {nonLinkExtra ? (
                <>
                  <StyledLi style={{ maxWidth: `calc(100% * (1 / ${ancestors.length + 1}))` }}>
                    <Chip color="white" href={uri}>
                      {page.title}
                    </Chip>

                    {children.length > 0 && (
                      <Dropdown>
                        <ul>
                          {children.map((child, i) => (
                            <li key={i}>
                              <Link href={child.uri}>
                                {CARET_RIGHT} <span>{child.title}</span>
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </Dropdown>
                    )}
                  </StyledLi>
                  <StyledLi style={{ maxWidth: `calc(100% * (1 / ${ancestors.length + 1}))` }}>
                    <Chip color="bgColor">{nonLinkExtra}</Chip>
                  </StyledLi>
                </>
              ) : (
                <StyledLi style={{ maxWidth: `calc(100% * (1 / ${ancestors.length + 1}))` }}>
                  <Chip color="bgColor">{page.title}</Chip>
                </StyledLi>
              )}
            </ul>
          </div>
        </div>
      </BreadcrumbsContainer>
    )
  }
}

export const BreadcrumbsContainer = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof ContainerReset>>(
  ({ className, ...props }, ref) => (
    <ContainerReset
      ref={ref}
      className={cn(
        "z-[1] bg-header-background w-full px-4 md:px-6",
        "[&_ul]:flex [&_ul]:items-center [&_ul]:type-label-caps [&_ul]:py-3",
        "[&_ul_li]:flex [&_ul_li]:items-center",
        "[&_ul_li]:after:content-['/'] [&_ul_li]:after:inline-block [&_ul_li]:after:px-1",
        "[&_ul_li_a]:max-w-full [&_ul_li_button]:max-w-full [&_ul_li_a]:whitespace-nowrap [&_ul_li_button]:whitespace-nowrap",
        "[&_ul_li_a]:overflow-hidden [&_ul_li_button]:overflow-hidden [&_ul_li_a]:text-ellipsis [&_ul_li_button]:text-ellipsis [&_ul_li_a]:justify-start [&_ul_li_button]:justify-start",
        "[&_ul_li_a_span]:max-w-full [&_ul_li_button_span]:max-w-full [&_ul_li_a_span]:whitespace-nowrap [&_ul_li_button_span]:whitespace-nowrap",
        "[&_ul_li_a_span]:overflow-hidden [&_ul_li_button_span]:overflow-hidden [&_ul_li_a_span]:text-ellipsis [&_ul_li_button_span]:text-ellipsis",
        "[&_ul_li_a_span]:justify-start [&_ul_li_button_span]:justify-start",
        "[&_ul_li:last-of-type]:after:hidden",
        className,
      )}
      {...props}
    />
  ),
)
BreadcrumbsContainer.displayName = "BreadcrumbsContainer"

const StyledLi = forwardRef<HTMLLIElement, ComponentPropsWithoutRef<"li">>(({ className, ...props }, ref) => (
  <li ref={ref} className={cn("relative hover:[&>div]:max-h-none", className)} {...props} />
))
StyledLi.displayName = "StyledLi"

const Dropdown = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "absolute top-full left-0 z-10 max-h-0 overflow-hidden",
      "[&_ul]:flex-col [&_ul]:items-start [&_ul]:p-2 [&_ul]:mt-2 [&_ul]:border [&_ul]:border-grey [&_ul]:bg-white [&_ul]:rounded-md",
      "[&_ul_li_a]:text-dark-grey [&_ul_li_a]:flex [&_ul_li_a]:items-center [&_ul_li_a]:pr-2",
      "[&_ul_li_a_svg]:w-[20px] [&_ul_li_a_svg]:h-[20px]",
      "[&_ul_li_a:hover_span]:underline",
      // Overrides BreadcrumbsContainer's `li:after` separator (content: "/")
      // for these nested dropdown list items, which are still descendants of
      // the outer breadcrumb `ul`. `!` guarantees this wins over that rule
      // regardless of the two components' generated-CSS order.
      "[&_ul_li]:after:!hidden",
      className,
    )}
    {...props}
  />
))
Dropdown.displayName = "Dropdown"
